import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Button,
  IconButton,
  Text,
  Badge,
  Tooltip,
  Collapse,
  useColorModeValue,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Flex,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Select,
  Switch,
  FormControl,
  FormLabel,
  Divider
} from '@chakra-ui/react';
import {
  Ruler,
  Calculator,
  Eye,
  Palette,
  ChartLine,
  MapPin,
  Camera,
  Sliders,
  ChartBar,
  ArrowsOutCardinal,
  Question,
  X,
  FloppyDisk,
  Share,
  Download
} from '@phosphor-icons/react';
import * as THREE from 'three';

const ThreeDAnalysisTools = ({ 
  scene,
  camera,
  renderer,
  controls,
  model,
  isVisible = true,
  onToggle,
  onMeasurement,
  onAnnotation
}) => {
  // State management
  const [activeTools, setActiveTools] = useState(new Set());
  const [measurements, setMeasurements] = useState([]);
  const [annotations, setAnnotations] = useState([]);
  const [visualAnalysis, setVisualAnalysis] = useState({
    curvature: false,
    slope: false,
    elevation: false,
    meshQuality: false
  });
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showMeasurementModal, setShowMeasurementModal] = useState(false);
  const [currentMeasurement, setCurrentMeasurement] = useState(null);

  // Refs for raycasting and interaction
  const raycaster = useRef(new THREE.Raycaster());
  const mouse = useRef(new THREE.Vector2());
  const measurementPoints = useRef([]);
  const measurementLine = useRef(null);
  const measurementGroup = useRef(new THREE.Group());

  // Theme colors
  const toolsBg = useColorModeValue('bg.accent', 'bg.accent');
  const borderColor = useColorModeValue('border.default', 'border.default');
  const textColor = useColorModeValue('text.default', 'text.default');
  const accentColor = useColorModeValue('brand.primary', 'brand.primary');

  // Initialize measurement group
  useEffect(() => {
    if (scene && measurementGroup.current) {
      scene.add(measurementGroup.current);
      measurementGroup.current.name = 'measurements';
    }
    
    return () => {
      if (scene && measurementGroup.current) {
        scene.remove(measurementGroup.current);
      }
    };
  }, [scene]);

  // Measurement tool functions
  const startMeasurement = () => {
    if (activeTools.has('measure')) {
      // Stop measuring - re-enable OrbitControls
      if (controls) {
        controls.enabled = true;
      }
      setActiveTools(prev => {
        const newSet = new Set(prev);
        newSet.delete('measure');
        return newSet;
      });
      finalizeMeasurement();
    } else {
      // Start measuring - disable OrbitControls to prevent conflicts
      if (controls) {
        controls.enabled = false;
      }
      setActiveTools(prev => new Set(prev).add('measure'));
      measurementPoints.current = [];
      
      if (measurementLine.current) {
        measurementGroup.current.remove(measurementLine.current);
        measurementLine.current = null;
      }
    }
  };

  const handleCanvasClick = (event) => {
    if (!activeTools.has('measure') || !renderer || !camera || !model) return;

    // Prevent event from bubbling to OrbitControls
    event.stopPropagation();
    event.preventDefault();

    const canvas = renderer.domElement;
    const rect = canvas.getBoundingClientRect();
    
    mouse.current.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    mouse.current.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

    raycaster.current.setFromCamera(mouse.current, camera);
    const intersects = raycaster.current.intersectObject(model, true);

    if (intersects.length > 0) {
      const point = intersects[0].point;
      measurementPoints.current.push(point.clone());

      // Create or update measurement line
      if (measurementPoints.current.length === 2) {
        const distance = measurementPoints.current[0].distanceTo(measurementPoints.current[1]);
        createMeasurementLine(measurementPoints.current[0], measurementPoints.current[1], distance);
        
        const newMeasurement = {
          id: Date.now(),
          start: measurementPoints.current[0].clone(),
          end: measurementPoints.current[1].clone(),
          distance: distance,
          unit: 'meters',
          timestamp: new Date()
        };
        
        setMeasurements(prev => [...prev, newMeasurement]);
        setCurrentMeasurement(newMeasurement);
        setShowMeasurementModal(true);
        
        if (onMeasurement) {
          onMeasurement(newMeasurement);
        }

        // Reset for next measurement
        measurementPoints.current = [];
      } else if (measurementPoints.current.length === 1) {
        // Show preview point
        createMeasurementPoint(point);
      }
    }
  };

  const createMeasurementPoint = (position) => {
    const geometry = new THREE.SphereGeometry(0.05);
    const material = new THREE.MeshBasicMaterial({ color: 0xff0000 });
    const sphere = new THREE.Mesh(geometry, material);
    sphere.position.copy(position);
    measurementGroup.current.add(sphere);
  };

  const createMeasurementLine = (start, end, distance) => {
    const geometry = new THREE.BufferGeometry().setFromPoints([start, end]);
    const material = new THREE.LineBasicMaterial({ color: 0xff0000, linewidth: 3 });
    const line = new THREE.Line(geometry, material);
    
    measurementGroup.current.add(line);
    measurementLine.current = line;

    // Add distance label
    const midpoint = new THREE.Vector3().addVectors(start, end).multiplyScalar(0.5);
    createDistanceLabel(midpoint, distance);
  };

  const createDistanceLabel = (position, distance) => {
    // Create a simple text sprite for the distance
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    canvas.width = 256;
    canvas.height = 64;
    
    context.font = 'Bold 24px Arial';
    context.fillStyle = 'rgba(255, 255, 255, 0.9)';
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.fillStyle = 'black';
    context.textAlign = 'center';
    context.fillText(`${distance.toFixed(2)}m`, canvas.width / 2, canvas.height / 2 + 8);

    const texture = new THREE.CanvasTexture(canvas);
    const spriteMaterial = new THREE.SpriteMaterial({ map: texture });
    const sprite = new THREE.Sprite(spriteMaterial);
    sprite.position.copy(position);
    sprite.scale.set(0.5, 0.125, 1);
    
    measurementGroup.current.add(sprite);
  };

  const finalizeMeasurement = () => {
    measurementPoints.current = [];
    if (measurementLine.current) {
      measurementLine.current = null;
    }
  };

  // Visual analysis functions
  const toggleCurvatureAnalysis = () => {
    if (!model) return;
    
    setVisualAnalysis(prev => {
      const newState = { ...prev, curvature: !prev.curvature };
      
      if (newState.curvature) {
        applyCurvatureVisualization();
      } else {
        resetModelMaterials();
      }
      
      return newState;
    });
  };

  const applyCurvatureVisualization = () => {
    model.traverse((child) => {
      if (child.isMesh && child.geometry) {
        // Create a simple curvature-based color visualization
        const geometry = child.geometry;
        const positions = geometry.attributes.position;
        const colors = new Float32Array(positions.count * 3);

        // Simple curvature approximation based on vertex normals
        if (geometry.attributes.normal) {
          const normals = geometry.attributes.normal;
          
          for (let i = 0; i < positions.count; i++) {
            // Simple curvature indicator: how much the normal deviates from surrounding normals
            const curvature = Math.random(); // Placeholder - would implement proper curvature calculation
            
            // Color based on curvature: blue (low) to red (high)
            colors[i * 3] = curvature; // R
            colors[i * 3 + 1] = 0.5 - curvature * 0.5; // G
            colors[i * 3 + 2] = 1 - curvature; // B
          }
          
          geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
          
          const material = new THREE.MeshBasicMaterial({ 
            vertexColors: true,
            side: THREE.DoubleSide
          });
          child.material = material;
        }
      }
    });
  };

  const toggleSlopeAnalysis = () => {
    if (!model) return;
    
    setVisualAnalysis(prev => {
      const newState = { ...prev, slope: !prev.slope };
      
      if (newState.slope) {
        applySlopeVisualization();
      } else {
        resetModelMaterials();
      }
      
      return newState;
    });
  };

  const applySlopeVisualization = () => {
    model.traverse((child) => {
      if (child.isMesh && child.geometry) {
        const geometry = child.geometry;
        const positions = geometry.attributes.position;
        const normals = geometry.attributes.normal;
        
        if (normals) {
          const colors = new Float32Array(positions.count * 3);
          const upVector = new THREE.Vector3(0, 1, 0);
          
          for (let i = 0; i < normals.count; i++) {
            const normal = new THREE.Vector3(
              normals.getX(i),
              normals.getY(i),
              normals.getZ(i)
            );
            
            // Calculate slope as angle from vertical
            const slope = Math.abs(normal.dot(upVector));
            
            // Color: green (flat) to red (steep)
            colors[i * 3] = 1 - slope; // R
            colors[i * 3 + 1] = slope; // G
            colors[i * 3 + 2] = 0; // B
          }
          
          geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
          
          const material = new THREE.MeshBasicMaterial({ 
            vertexColors: true,
            side: THREE.DoubleSide
          });
          child.material = material;
        }
      }
    });
  };

  const resetModelMaterials = () => {
    if (!model) return;
    
    model.traverse((child) => {
      if (child.isMesh) {
        // Reset to default material
        if (child.userData.originalMaterial) {
          child.material = child.userData.originalMaterial;
        } else {
          const material = new THREE.MeshLambertMaterial({
            color: 0x8B4513,
            side: THREE.DoubleSide
          });
          child.material = material;
        }
        
        // Remove vertex colors if they exist
        if (child.geometry.attributes.color) {
          child.geometry.deleteAttribute('color');
        }
      }
    });
  };

  const clearAllMeasurements = () => {
    setMeasurements([]);
    measurementGroup.current.clear();
  };

  const exportMeasurements = () => {
    const data = {
      measurements: measurements,
      annotations: annotations,
      timestamp: new Date().toISOString(),
      modelInfo: model ? {
        name: model.name,
        vertices: getVertexCount(model),
        faces: getFaceCount(model)
      } : null
    };
    
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `megalith-analysis-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Helper functions
  const getVertexCount = (model) => {
    let count = 0;
    model.traverse((child) => {
      if (child.isMesh && child.geometry) {
        count += child.geometry.attributes.position?.count || 0;
      }
    });
    return count;
  };

  const getFaceCount = (model) => {
    let count = 0;
    model.traverse((child) => {
      if (child.isMesh && child.geometry) {
        if (child.geometry.index) {
          count += child.geometry.index.count / 3;
        } else {
          count += (child.geometry.attributes.position?.count || 0) / 3;
        }
      }
    });
    return Math.floor(count);
  };

  // Event listeners
  useEffect(() => {
    const canvas = renderer?.domElement;
    if (canvas && activeTools.has('measure')) {
      canvas.addEventListener('click', handleCanvasClick);
      return () => canvas.removeEventListener('click', handleCanvasClick);
    }
  }, [activeTools, renderer, camera, model]);

  if (!isVisible) return null;

  return (
    <>
      <Box
        position="absolute"
        top={2}
        left={2}
        bg={toolsBg}
        borderRadius="md"
        p={2}
        border="1px"
        borderColor={borderColor}
        minW="200px"
        maxW="300px"
      >
        <VStack spacing={3} align="stretch">
          {/* Header */}
          <Flex justify="space-between" align="center">
            <Text fontWeight="bold" fontSize="sm" color={textColor}>
              Analysis Tools
            </Text>
            <IconButton
              icon={onToggle ? <X /> : <ArrowsOutCardinal />}
              size="xs"
              variant="ghost"
              onClick={onToggle}
            />
          </Flex>

          {/* Measurement Tools */}
          <VStack spacing={2} align="stretch">
            <Text fontSize="xs" fontWeight="semibold" color={textColor}>
              Measurement
            </Text>
            <HStack spacing={1}>
              <Tooltip label="Distance measurement">
                <Button
                  leftIcon={<Ruler />}
                  size="sm"
                  variant={activeTools.has('measure') ? 'solid' : 'outline'}
                  colorScheme={activeTools.has('measure') ? 'blue' : 'gray'}
                  onClick={startMeasurement}
                  flex={1}
                >
                  Measure
                </Button>
              </Tooltip>
              {measurements.length > 0 && (
                <Tooltip label="Clear measurements">
                  <IconButton
                    icon={<X />}
                    size="sm"
                    variant="outline"
                    onClick={clearAllMeasurements}
                  />
                </Tooltip>
              )}
            </HStack>
            
            {measurements.length > 0 && (
              <Badge variant="subtle" fontSize="xs">
                {measurements.length} measurement{measurements.length !== 1 ? 's' : ''}
              </Badge>
            )}
          </VStack>

          <Divider />

          {/* Visual Analysis */}
          <VStack spacing={2} align="stretch">
            <Text fontSize="xs" fontWeight="semibold" color={textColor}>
              Visual Analysis
            </Text>
            <VStack spacing={1}>
              <FormControl display="flex" alignItems="center">
                <FormLabel htmlFor="curvature" mb="0" fontSize="xs" flex={1}>
                  Curvature
                </FormLabel>
                <Switch
                  id="curvature"
                  size="sm"
                  isChecked={visualAnalysis.curvature}
                  onChange={toggleCurvatureAnalysis}
                />
              </FormControl>
              
              <FormControl display="flex" alignItems="center">
                <FormLabel htmlFor="slope" mb="0" fontSize="xs" flex={1}>
                  Slope
                </FormLabel>
                <Switch
                  id="slope"
                  size="sm"
                  isChecked={visualAnalysis.slope}
                  onChange={toggleSlopeAnalysis}
                />
              </FormControl>
            </VStack>
          </VStack>

          <Divider />

          {/* Export Tools */}
          <HStack spacing={1}>
            <Tooltip label="Export analysis data">
              <IconButton
                icon={<Download />}
                size="sm"
                variant="outline"
                onClick={exportMeasurements}
                isDisabled={measurements.length === 0}
              />
            </Tooltip>
            <Tooltip label="Save analysis">
              <IconButton
                icon={<FloppyDisk />}
                size="sm"
                variant="outline"
                isDisabled={measurements.length === 0}
              />
            </Tooltip>
          </HStack>

          {/* Advanced Tools Toggle */}
          <Button
            size="xs"
            variant="ghost"
            onClick={() => setShowAdvanced(!showAdvanced)}
            rightIcon={showAdvanced ? <X /> : <Sliders />}
          >
            Advanced
          </Button>

          <Collapse in={showAdvanced} animateOpacity>
            <VStack spacing={2} align="stretch" pt={2}>
              <Text fontSize="xs" fontWeight="semibold" color={textColor}>
                Advanced Analysis
              </Text>
              <Button leftIcon={<ChartBar />} size="xs" variant="outline">
                Mesh Quality
              </Button>
              <Button leftIcon={<ChartLine />} size="xs" variant="outline">
                Cross Section
              </Button>
              <Button leftIcon={<Calculator />} size="xs" variant="outline">
                Volume Calc
              </Button>
            </VStack>
          </Collapse>
        </VStack>
      </Box>

      {/* Measurement Details Modal */}
      <Modal isOpen={showMeasurementModal} onClose={() => setShowMeasurementModal(false)}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Measurement Details</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {currentMeasurement && (
              <VStack spacing={3} align="stretch">
                <Text>
                  <strong>Distance:</strong> {currentMeasurement.distance.toFixed(3)} meters
                </Text>
                <Text>
                  <strong>Start Point:</strong> ({currentMeasurement.start.x.toFixed(2)}, {currentMeasurement.start.y.toFixed(2)}, {currentMeasurement.start.z.toFixed(2)})
                </Text>
                <Text>
                  <strong>End Point:</strong> ({currentMeasurement.end.x.toFixed(2)}, {currentMeasurement.end.y.toFixed(2)}, {currentMeasurement.end.z.toFixed(2)})
                </Text>
                <Text fontSize="sm" color="gray.500">
                  Measured at {currentMeasurement.timestamp.toLocaleTimeString()}
                </Text>
              </VStack>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

export default ThreeDAnalysisTools;
